import i18n from 'i18next';
import { intlFormat, startOfMonth, endOfMonth, addDays, getDay, getDate } from 'date-fns';

import { getDateString } from '@/exceed';

const DAY_MAPPING = {
  su: 'sunday',
  mo: 'monday',
  tu: 'tuesday',
  we: 'wednesday',
  th: 'thursday',
  fr: 'friday',
  sa: 'saturday',
};

const pr = new Intl.PluralRules(document.documentElement.lang, { type: 'ordinal' });

const getOrdinals = (n) => {
  const suffixes = new Map([
    ['one', i18n.t('ordinal-one-suffix')],
    ['two', i18n.t('ordinal-two-suffix')],
    ['few', i18n.t('ordinal-few-suffix')],
    ['other', i18n.t('ordinal-other-suffix')],
  ]);

  const rule = pr.select(n);
  const suffix = suffixes.get(rule);
  return `${suffix}`;
};

// Used to figure out how many times a specific day has occurred  in a month.
function getNthOccurrence(date, targetDay) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const start = startOfMonth(new Date(year, month));
  const end = endOfMonth(start);

  let count = 0;

  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (getDay(d) === targetDay) {
      count++;
      if (getDate(d) === getDate(date)) {
        return i18n.t('count-ordinal', {
          count,
          ordinal: getOrdinals(Number(count)),
        });
      }
    }
  }
}

export default ({ frequency, interval, until, days, startDate, startTime, by }) => {
  const untilDateString = getDateString(until);

  const translateDays = (daysMap) =>
    Object.keys(daysMap)
      .filter((day) => daysMap[day])
      .map((day) => i18n.t(DAY_MAPPING[day]));

  // WEEKLY REPEATING AGENDA ITEMS
  if (frequency === 'weekly') {
    const translatedDays = translateDays(days);
    const formatTranslatedDays = new Intl.ListFormat(document.documentElement.lang, {
      style: 'long',
      type: 'conjunction',
    });

    const daysText = formatTranslatedDays.format(translatedDays);

    // "Repeats every 1 week on Sunday until 05/14/2023" or "Repeats every 2 weeks on Sunday, Monday and Tuesday until 05/14/2023"
    return i18n.t('repeats-until-weekly-tooltip', {
      count: interval,
      days: daysText,
      until: untilDateString,
    });
  }

  // MONTHLY REPEATING AGENDA ITEMS
  if (frequency === 'monthly') {
    const date = new Date(`${startDate}T${startTime}Z`);

    if (by === 'day') {
      const nth = getNthOccurrence(date, getDay(date));

      // "Repeats on the 3rd Monday of every month until 05/14/2023" or "Repeats on the 3rd Monday of every 2 months until 05/14/2023"
      return i18n.t('repeats-until-monthly-day-tooltip', {
        occurence: nth,
        day: i18n.t(DAY_MAPPING[Object.keys(DAY_MAPPING)[getDay(date)]]),
        until: untilDateString,
        count: interval,
      });
    }

    const formattedDate = intlFormat(
      date,
      { day: 'numeric' },
      { locale: document.documentElement.lang || 'en' },
    );

    // "Repeats on the 15th of every month until 05/14/2023" or "Repeats on the 15th of every 2 months until 05/14/2023"
    return i18n.t('repeats-until-monthly-tooltip', {
      day: i18n.t('count-ordinal', {
        count: formattedDate,
        ordinal: getOrdinals(Number(formattedDate)),
      }),
      until: untilDateString,
      count: interval,
    });
  }

  // DAILY REPEATING AGENDA ITEMS

  // "Repeats every 1 day until 05/14/2023" or "Repeats every 2 days until 05/14/2023"
  return i18n.t(`repeats-until-${frequency}-tooltip`, {
    count: interval,
    date: untilDateString,
  });
};
